import React from "react";
import "./Markdown.css";

/* Lightweight, dependency-free markdown renderer for assistant replies.
   Handles: ### headings, **bold**, *italic*, `code`, - / * bullet lists,
   1. numbered lists, | tables |, and paragraphs. */

let _k = 0;
const key = () => `md-${_k++}`;

const _hideImg = (e) => { e.currentTarget.style.display = "none"; };

/* Inline: images ![alt](url), links [text](url), bare image URLs, **bold**, `code`, *italic* */
function renderInline(text) {
  if (text == null) return null;
  const nodes = [];
  const re = /(!\[([^\]]*)\]\(([^)\s]+)\))|(\[([^\]]+)\]\(([^)\s]+)\))|(\*\*([^*]+)\*\*)|(`([^`]+)`)|(\*([^*]+)\*)|(https?:\/\/[^\s)]+\.(?:jpe?g|png|gif|webp|svg)(?:\?[^\s)]*)?)/gi;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      nodes.push(<img key={key()} className="md-img" src={m[3]} alt={m[2] || ""} loading="lazy" onError={_hideImg} />);
    } else if (m[4] !== undefined) {
      nodes.push(<a key={key()} className="md-link" href={m[6]} target="_blank" rel="noreferrer">{m[5]}</a>);
    } else if (m[8] !== undefined) {
      nodes.push(<strong key={key()}>{m[8]}</strong>);
    } else if (m[10] !== undefined) {
      nodes.push(<code key={key()} className="md-code">{m[10]}</code>);
    } else if (m[12] !== undefined) {
      nodes.push(<em key={key()}>{m[12]}</em>);
    } else if (m[13] !== undefined) {
      nodes.push(<img key={key()} className="md-img" src={m[13]} alt="" loading="lazy" onError={_hideImg} />);
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function splitRow(line) {
  return line
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());
}

export default function Markdown({ text, className = "" }) {
  if (!text) return null;
  const lines = String(text).replace(/\r/g, "").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    let line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) { i++; continue; }

    // Heading
    const h = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const lvl = Math.min(h[1].length, 4);
      blocks.push(
        <div key={key()} className={`md-h md-h${lvl}`}>{renderInline(h[2])}</div>
      );
      i++;
      continue;
    }

    // Table: a line with | followed by a separator row (---)
    if (trimmed.includes("|") && i + 1 < lines.length && /^[\s|:-]+$/.test(lines[i + 1].trim()) && lines[i + 1].includes("-")) {
      const header = splitRow(trimmed);
      i += 2;
      const body = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) {
        body.push(splitRow(lines[i].trim()));
        i++;
      }
      blocks.push(
        <div key={key()} className="md-table-wrap">
          <table className="md-table">
            <thead><tr>{header.map((c) => <th key={key()}>{renderInline(c)}</th>)}</tr></thead>
            <tbody>{body.map((r) => <tr key={key()}>{r.map((c) => <td key={key()}>{renderInline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }

    // Bullet list
    if (/^[-*•]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) {
        items.push(lines[i].trim().replace(/^[-*•]\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={key()} className="md-ul">
          {items.map((it) => <li key={key()}>{renderInline(it)}</li>)}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push(
        <ol key={key()} className="md-ol">
          {items.map((it) => <li key={key()}>{renderInline(it)}</li>)}
        </ol>
      );
      continue;
    }

    // Paragraph (gather until blank line)
    const para = [trimmed];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|[-*•]\s|\d+\.\s)/.test(lines[i].trim())) {
      para.push(lines[i].trim());
      i++;
    }
    blocks.push(<p key={key()} className="md-p">{renderInline(para.join(" "))}</p>);
  }

  return <div className={`md ${className}`}>{blocks}</div>;
}
